// ABOUTME: Unified authentication service using provider abstraction
// ABOUTME: Application-level auth API that works with any backend provider

import { getProvider } from '@/providers'
import type { User, Session, AuthResult } from '@/providers'

/**
 * Authentication service
 *
 * Provides high-level authentication operations using the configured provider
 */
export class AuthService {
  /**
   * Sign up a new user
   */
  async signUp(
    email: string,
    password: string,
    // biome-ignore lint/suspicious/noExplicitAny: dynamic user preferences
    metadata?: { displayName?: string; preferences?: any }
  ): Promise<AuthResult> {
    const provider = getProvider()
    return provider.auth.signUp(email, password, metadata)
  }

  /**
   * Sign in an existing user
   */
  async signIn(email: string, password: string): Promise<AuthResult> {
    const provider = getProvider()
    return provider.auth.signIn(email, password)
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    const provider = getProvider()
    await provider.auth.signOut()
  }

  /**
   * Get the current session
   */
  async getSession(): Promise<Session | null> {
    const provider = getProvider()
    return provider.auth.getSession()
  }

  /**
   * Get the current user
   */
  async getCurrentUser(): Promise<User | null> {
    const provider = getProvider()
    return provider.auth.getUser()
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    const session = await this.getSession()
    return session !== null
  }

  /**
   * Subscribe to authentication state changes
   *
   * @param callback Called when auth state changes
   * @returns Unsubscribe function
   */
  onAuthStateChange(callback: (user: User | null) => void): () => void {
    const provider = getProvider()
    return provider.auth.onAuthStateChange((_session, user) => {
      callback(user)
    })
  }

  /**
   * Send password reset email
   */
  async resetPassword(email: string): Promise<{ error?: Error }> {
    const provider = getProvider()
    return provider.auth.resetPassword(email)
  }

  /**
   * Update user profile metadata
   */
  // biome-ignore lint/suspicious/noExplicitAny: dynamic profile metadata
  async updateProfile(metadata: Record<string, any>): Promise<{ user: User | null; error?: Error }> {
    const provider = getProvider()
    return provider.auth.updateUser(metadata)
  }
}

// Singleton instance
let authServiceInstance: AuthService | null = null

/**
 * Get the auth service instance
 */
export function getAuthService(): AuthService {
  if (!authServiceInstance) {
    authServiceInstance = new AuthService()
  }
  return authServiceInstance
}
